import apiResponse from './google-fonts-data.json'

export interface GoogleFont {
  family: string
  category: 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace'
  variants: string[]
  subsets: string[]
}

interface APIFont {
  family: string
  variants: string[]
  subsets: string[]
  category: string
}

// Parse variants to extract weights (filter out italic variants, map 'regular' to '400')
function parseWeights(variants: string[]): string[] {
  const weights = new Set<string>()

  for (const variant of variants) {
    if (variant === 'regular') {
      weights.add('400')
    } else if (variant === 'italic') {
      // Skip standalone italic
      continue
    } else if (/^\d+$/.test(variant)) {
      weights.add(variant)
    } else if (/^\d+italic$/.test(variant)) {
      // Extract weight from italic variant like "700italic"
      weights.add(variant.replace('italic', ''))
    }
  }

  // Sort weights numerically
  return Array.from(weights).sort((a, b) => Number(a) - Number(b))
}

// Transform API response to our format, filtering to Latin-supporting fonts only
export const GOOGLE_FONTS: GoogleFont[] = (apiResponse as APIFont[])
  .filter(font => {
    // Only include fonts that support Latin characters
    return font.subsets.includes('latin')
  })
  .map(font => ({
    family: font.family,
    category: font.category as GoogleFont['category'],
    variants: parseWeights(font.variants),
    subsets: font.subsets,
  }))
  .sort((a, b) => a.family.localeCompare(b.family))

// Popular/featured fonts - shown first when no search query
export const FEATURED_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Source Sans 3', 'Nunito', 'Raleway', 'Work Sans', 'DM Sans', 'Manrope',
  'Playfair Display', 'Merriweather', 'Lora', 'PT Serif', 'Crimson Pro',
  'Bebas Neue', 'Anton', 'Oswald', 'Abril Fatface',
  'Dancing Script', 'Pacifico', 'Caveat', 'Great Vibes',
  'Fira Code', 'JetBrains Mono', 'Source Code Pro', 'IBM Plex Mono',
]

export function buildGoogleFontsUrl(families: string[]): string {
  const params = families
    .map(f => `family=${encodeURIComponent(f)}:wght@100;200;300;400;500;600;700;800;900`)
    .join('&')
  return `https://fonts.googleapis.com/css2?${params}&display=swap`
}

export const CATEGORIES = ['all', 'sans-serif', 'serif', 'display', 'handwriting', 'monospace'] as const
export type Category = typeof CATEGORIES[number]

// Stats for display
export const FONT_STATS = {
  total: GOOGLE_FONTS.length,
  byCategory: CATEGORIES.slice(1).reduce((acc, cat) => {
    acc[cat] = GOOGLE_FONTS.filter(f => f.category === cat).length
    return acc
  }, {} as Record<string, number>)
}
